/**
 * ============================================================================
 * HeldBookingsModal - SHOWS BOOKINGS CURRENTLY HELD
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. THIS IS NEARLY IDENTICAL TO AvailableBookingsModal
 *    - Same structure, same pattern
 *    - Only differences: hook, title, color, "Releases In" column
 *    - If you change one modal, consider if other needs same change
 *
 * 2. HELD BOOKINGS ARE TEMPORARY
 *    - These bookings are held until booking end time
 *    - Then they automatically move to available balance
 *    - Show "releases_in" field to inform user when money releases
 *
 * 3. ORANGE COLOR SCHEME
 *    - Held = waiting/pending state (orange)
 *    - Available = ready for payout (green)
 *    - This visual distinction is important
 *
 * 4. FETCH ON MODAL OPEN
 *    - Same pattern as AvailableBookingsModal
 *    - No pre-fetching in parent
 *    - Keeps parent component clean
 *
 * ============================================================================
 */

import React from 'react';
import { Clock, X } from 'lucide-react';
import { useHeldBookings, type PayoutBooking } from '../../../hooks/usePayoutBookings';
import { formatCurrency, formatDate } from '../../../utils/revenueFormatters';
import Modal from '../../ui/Modal';

interface HeldBookingsModalProps {
  venueId: number;
  onClose: () => void;
  isOpen?: boolean;
}

/**
 * Modal showing bookings that are currently held (will be released after booking ends)
 */
export function HeldBookingsModal({ venueId, onClose, isOpen = true }: HeldBookingsModalProps) {
  // TanStack Query automatically fetches when modal opens
  const { data: bookings, isPending, error } = useHeldBookings(venueId);

  // Calculate total
  const totalAmount = (bookings || []).reduce((sum, booking) => {
    return sum + parseFloat(String(booking.venue_share || 0));
  }, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" animate={false}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Held Balance Bookings</h3>
            {bookings && (
              <p className="text-sm text-gray-600 mt-1">
                {bookings.length} booking{bookings.length !== 1 ? 's' : ''} currently held (released after booking ends)
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {isPending ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
              <p className="text-gray-600">Loading bookings...</p>
            </div>
          ) : error ? (
            // Error state
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-red-900 font-semibold mb-2">Failed to load bookings</p>
              <p className="text-red-700 text-sm">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
          ) : !bookings || bookings.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12">
              <Clock className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-600 font-semibold">No bookings held</p>
              <p className="text-gray-500 text-sm mt-1">
                Bookings are held until they complete, then moved to available balance
              </p>
            </div>
          ) : (
            // Bookings list
            <div className="space-y-3">
              {bookings.map((booking: PayoutBooking) => (
                <div
                  key={booking.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      {/* Booking Info */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Booking</p>
                        <p className="font-semibold text-gray-900 text-sm">
                          {booking.booking_reference}
                        </p>
                        <p className="text-xs text-gray-600">{booking.court_name}</p>
                      </div>

                      {/* Date & Time */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Date & Time</p>
                        <p className="text-sm text-gray-900">
                          {formatDate(booking.booking_date)}
                        </p>
                        {booking.start_time && booking.end_time && (
                          <p className="text-xs text-gray-600">
                            {booking.start_time.substring(0, 5)} - {booking.end_time.substring(0, 5)}
                          </p>
                        )}
                      </div>

                      {/* Customer */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Customer</p>
                        <p className="text-sm text-gray-900">
                          {booking.customer_name || 'N/A'}
                        </p>
                      </div>

                      {/* Releases At */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Releases At</p>
                        {booking.releases_in ? (
                          <p className="text-sm text-orange-600 font-semibold">
                            {booking.releases_in}
                          </p>
                        ) : booking.end_time ? (
                          <p className="text-sm text-orange-600 font-semibold">
                            {booking.end_time.substring(0, 5)}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">Soon</p>
                        )}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="text-right sm:ml-6">
                      <p className="text-xs text-gray-500 mb-1">Your Share</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(booking.venue_share)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {bookings && bookings.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <p className="font-bold text-gray-900">
                Total ({bookings.length} booking{bookings.length !== 1 ? 's' : ''}):
              </p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(totalAmount)}
              </p>
            </div>
          </div>
        )}
    </Modal>
  );
}
